import React, { useEffect, useState, Fragment } from "react";
import { Row, Col, Card, Button, Modal, Form, Badge, Spinner } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const ProductCategoryMaster = () => {
 const [list, setList] = useState([]);
 const [filtered, setFiltered] = useState([]);
 const [loading, setLoading] = useState(false);
 const [saving, setSaving] = useState(false);

 const [showModal, setShowModal] = useState(false);
 const [editId, setEditId] = useState(null);
 const [search, setSearch] = useState("");

 const [form, setForm] = useState({
 name: "",
 description: "",
 status: "Active",
 });

 /* =========================
 FETCH CATEGORIES
 ========================= */
 const fetchCategories = async () => {
 setLoading(true);
 try {
 const res = await api.get("/product-categories?includeInactive=true");
 if (res.data.success) {
 setList(res.data.data);
 setFiltered(res.data.data);
 }
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchCategories();
 }, []);

 /* =========================
 SEARCH FILTER
 ========================= */
 useEffect(() => {
 if (!search.trim()) {
 setFiltered(list);
 } else {
 const s = search.toLowerCase();
 setFiltered(list.filter((i) => i.name.toLowerCase().includes(s)));
 }
 }, [search, list]);

 /* =========================
 MODAL HANDLERS
 ========================= */
 const openAdd = () => {
 setEditId(null);
 setForm({ name: "", description: "", status: "Active" });
 setShowModal(true);
 };

 const openEdit = (item) => {
 setEditId(item._id);
 setForm({
 name: item.name,
 description: item.description || "",
 status: item.status,
 });
 setShowModal(true);
 };

 const closeModal = () => {
 if (!saving) setShowModal(false);
 };

 const handleChange = (e) =>
 setForm({ ...form, [e.target.name]: e.target.value });

 /* =========================
 SAVE CATEGORY
 ========================= */
 const handleSave = async () => {
 if (!form.name.trim()) {
 return swal("Validation", "Category name is required", "warning");
 }

 try {
 setSaving(true);

 if (editId) {
 await api.put(`/product-categories/${editId}`, form);
 } else {
 await api.post("/product-categories", form);
 }

 swal("Success", "Category saved successfully", "success");

 closeModal();
 fetchCategories();
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Operation failed",
 "error"
 );
 } finally {
 setSaving(false);
 }
 };

 /* =========================
 DEACTIVATE CATEGORY
 ========================= */
 const deactivate = (item) => {
 swal({
 title: `Deactivate "${item.name}"?`,
 text: "This category will no longer be selectable.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (ok) => {
 if (!ok) return;

 try {
 await api.delete(`/product-categories/${item._id}`);
 fetchCategories();
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Cannot deactivate",
 "error"
 );
 }
 });
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Product Category Master" motherMenu="Masters" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Product Categories</Card.Title>
 <Button onClick={openAdd}>+ Add Category</Button>
 </Card.Header>

 <Card.Body>

 {/* SEARCH */}
 <div className="mb-3">
 <Form.Control
 placeholder="Search category..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 </div>

 {/* LOADING */}
 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 ) : (
 <table className="table table-bordered table-hover">
 <thead className="table-light">
 <tr>
 <th>Name</th>
 <th>Description</th>
 <th>Status</th>
 <th width="170">Actions</th>
 </tr>
 </thead>

 <tbody>
 {filtered.map((item) => (
 <tr key={item._id}>
 <td>{item.name}</td>

 <td>{item.description || "-"}</td>

 <td>
 <Badge
 bg={
 item.status === "Active"
 ? "success"
 : "secondary"
 }
 >
 {item.status}
 </Badge>
 </td>

 <td>
 <Button
 size="sm"
 variant="warning"
 className="me-2"
 onClick={() => openEdit(item)}
 >
 Edit
 </Button>

 {item.status === "Active" && (
 <Button
 size="sm"
 variant="danger"
 onClick={() => deactivate(item)}
 >
 Deactivate
 </Button>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}

 {!loading && filtered.length === 0 && (
 <div className="text-center text-muted py-4">
 No categories found
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ================= MODAL ================= */}

 <Modal show={showModal} onHide={closeModal} backdrop={saving ? "static" : true}>
 <Modal.Header closeButton={!saving}>
 <Modal.Title>
 {editId ? "Edit Category" : "Add Category"}
 </Modal.Title>
 </Modal.Header>

 <Modal.Body>
 <Form.Group className="mb-3">
 <Form.Label>Name *</Form.Label>
 <Form.Control
 name="name"
 value={form.name}
 onChange={handleChange}
 placeholder="e.g. Clothing"
 disabled={saving}
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>Description</Form.Label>
 <Form.Control
 name="description"
 value={form.description}
 onChange={handleChange}
 as="textarea"
 rows={2}
 disabled={saving}
 />
 </Form.Group>

 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select
 name="status"
 value={form.status}
 onChange={handleChange}
 disabled={saving}
 >
 <option value="Active">Active</option>
 <option value="Inactive">Inactive</option>
 </Form.Select>
 </Form.Group>
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={closeModal} disabled={saving}>
 Cancel
 </Button>

 <Button onClick={handleSave} disabled={saving}>
 {saving ? "Saving..." : "Save"}
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default ProductCategoryMaster;